import type { Clock } from "@riffbox/protocol";
import { computeTargetPosition } from "./clock.js";
import { driftDecision, clamp, DEFAULT_DRIFT, type DriftConfig } from "./drift.js";

/**
 * The sync controller — one decision per loop tick. It folds together the
 * reliable-follow rules (see docs/TESTING.md): event-exact, tolerant, and
 * crucially **buffer-aware** so a network stall can't trigger a seek-into-the-void
 * thrash loop. The client and the convergence simulator share this code path.
 *
 *  - paused      → snap to the exact frame (a paused element can't be slewed)
 *  - buffering   → "wait": do nothing, let the network recover (no seek/slew)
 *  - within band → nominal 1.0×
 *  - moderate    → gentle slew
 *  - far + ready + off cooldown → a single hard seek (then cooldown)
 *  - far + in cooldown          → slew toward target instead of re-seeking
 */
export type SyncOutcome =
  | { kind: "seek"; seekToSec: number }
  | { kind: "slew"; playbackRate: number }
  | { kind: "nominal" }
  | { kind: "wait" };

/** What the controller needs to know about the media element this tick. */
export interface PlayerView {
  currentSec: number;
  /** True when the element can't play forward (readyState < HAVE_FUTURE_DATA / stalled). */
  buffering: boolean;
  /** Milliseconds since the last hard seek (for the seek cooldown). */
  msSinceSeek: number;
}

export interface SyncTick {
  paused: boolean;
  /** Authoritative target position at this instant. */
  targetSec: number;
  outcome: SyncOutcome;
}

function slewToward(targetSec: number, currentSec: number, cfg: DriftConfig): SyncOutcome {
  const delta = targetSec - currentSec;
  return { kind: "slew", playbackRate: 1 + clamp(delta * cfg.gainK, -cfg.maxRateDelta, cfg.maxRateDelta) };
}

export function syncTick(
  clock: Clock,
  estServerNowMs: number,
  view: PlayerView,
  cfg: DriftConfig = DEFAULT_DRIFT,
): SyncTick {
  const targetSec = computeTargetPosition(clock, estServerNowMs);

  // Not playing (paused, or "preparing" during the readiness gate): hold frozen
  // at the position, snapping there when off (a held element can't be slewed).
  if (clock.state !== "playing") {
    const needsSnap = Math.abs(targetSec - view.currentSec) > cfg.slewDeadbandSec;
    return {
      paused: true,
      targetSec,
      outcome: needsSnap ? { kind: "seek", seekToSec: targetSec } : { kind: "nominal" },
    };
  }

  const d = driftDecision(targetSec, view.currentSec, cfg);

  if (d.kind === "seek") {
    // Far off (a join, a host seek, or recovery from a long stall). Seek at most
    // once per cooldown — that bounds any seek-into-a-stall to one attempt every
    // few seconds instead of a tight thrash loop. (The first seek fires
    // immediately: msSinceSeek starts at +Infinity.)
    if (view.msSinceSeek >= cfg.seekCooldownMs) {
      return { paused: false, targetSec, outcome: { kind: "seek", seekToSec: d.seekToSec } };
    }
    // Within the cooldown: don't re-seek. Hold while buffering, else slew closer.
    if (view.buffering) return { paused: false, targetSec, outcome: { kind: "wait" } };
    return { paused: false, targetSec, outcome: slewToward(targetSec, view.currentSec, cfg) };
  }

  if (d.kind === "slew") {
    // Don't fight the network: while buffering, hold and let it recover.
    if (view.buffering) return { paused: false, targetSec, outcome: { kind: "wait" } };
    return { paused: false, targetSec, outcome: { kind: "slew", playbackRate: d.playbackRate } };
  }

  return { paused: false, targetSec, outcome: { kind: "nominal" } };
}
